/**
 * Motion layer.
 *
 * Wires the animation vocabulary in js/motion-sequences.js up to the page
 * with GSAP. The four sequences and what each one references are documented
 * there; this file only decides which elements get which, and when.
 *
 * A summary, so the wiring reads on its own:
 *
 *   rollout    progressive delivery — a canary, then doubling waves
 *   pipeline   CI/CD stages — strictly sequential, markers going green
 *   provision  replicas under an autoscaler — near-simultaneous, overshooting
 *   logStream  a log tail — uniform, quick, uncapped
 *
 * Two hard rules hold across all of it:
 *
 *   1. Nothing is hidden by CSS that only JavaScript can un-hide. Every
 *      starting state is written by gsap.set() at boot, so if this module
 *      fails to load, is blocked, or throws, the page renders in its final
 *      state instead of a blank one.
 *   2. Decoration can never permanently hide content. A watchdog force-lands
 *      anything still waiting after four seconds, whatever the reason.
 */

// The core entry with the two plugins this file uses registered explicitly.
// Worth knowing before trying to trim it: this is NOT smaller than importing
// the 'gsap' barrel — measured at the same 53 kB gzip either way, because
// CSSPlugin and the standard eases are most of what the barrel is, and
// ScrollTrigger is imported here regardless. The explicit form is kept for
// legibility, not for bundle size.
import { gsap } from 'gsap/gsap-core';
import { CSSPlugin } from 'gsap/CSSPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { SEQUENCES, resolveSequence, stageDelays } from './motion-sequences.js';

gsap.registerPlugin(CSSPlugin, ScrollTrigger);

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** How long decoration gets before the watchdog lands it anyway. */
const WATCHDOG_MS = 4000;

/* ==========================================================================
   Browser wiring
   ========================================================================== */

/** Put a group into its landed state immediately, with no animation. */
function land(elements) {
  gsap.set(elements, { clearProps: 'opacity,transform' });
  for (const element of elements) element.classList.add('is-revealed');
}

/**
 * Collect the animation groups on the page.
 * A `[data-reveal-group]` container animates its own `[data-reveal]`
 * descendants together; anything left over is animated on its own.
 *
 * @returns {{ container: Element, targets: Element[], sequence: keyof SEQUENCES }[]}
 */
function collectGroups() {
  const groups = [];
  const claimed = new Set();

  for (const container of document.querySelectorAll('[data-reveal-group]')) {
    const targets = [...container.querySelectorAll('[data-reveal]')];
    if (!targets.length) continue;
    for (const target of targets) claimed.add(target);
    groups.push({
      container,
      targets,
      sequence: resolveSequence(container.getAttribute('data-motion')),
    });
  }

  for (const target of document.querySelectorAll('[data-reveal]')) {
    if (claimed.has(target)) continue;
    groups.push({
      container: target,
      targets: [target],
      sequence: resolveSequence(target.getAttribute('data-motion')),
    });
  }

  return groups;
}

/**
 * Animate the pipeline markers from pending to passed, in step with their
 * stages. `--stage-pass` defaults to 1 in CSS, so a marker is already in its
 * passed state before this runs and stays there if it never does.
 */
function passStages(container, delays) {
  const markers = [...container.querySelectorAll('[data-stage-marker]')];
  if (!markers.length) return;

  gsap.set(markers, { '--stage-pass': 0 });
  markers.forEach((marker, index) => {
    gsap.to(marker, {
      '--stage-pass': 1,
      duration: 0.34,
      ease: 'none',
      delay: (delays[index] ?? 0) + 0.18,
    });
  });
}

/** Reveal each group as it reaches the viewport. */
function initReveal() {
  const groups = collectGroups();
  if (!groups.length) return 0;

  let count = 0;

  for (const { container, targets, sequence } of groups) {
    const config = SEQUENCES[sequence];
    const delays = stageDelays(targets.length, sequence);
    count += targets.length;

    // The starting state is written here, in JavaScript, and nowhere else.
    // That is what makes a failure to load render the finished page rather
    // than an empty one.
    gsap.set(targets, { ...config.from });

    ScrollTrigger.create({
      trigger: container,
      start: 'top 88%',
      once: true,
      onEnter: () => {
        targets.forEach((target, index) => {
          gsap.to(target, {
            opacity: 1,
            y: 0,
            x: 0,
            scale: 1,
            rotateX: 0,
            duration: config.duration,
            ease: config.ease,
            delay: delays[index],
            // Leaving a transform on the element would sit on top of the
            // pointer-tilt engine's own transform for the rest of the session.
            onComplete: () => {
              gsap.set(target, { clearProps: 'transform' });
              target.classList.add('is-revealed');
            },
          });
        });

        passStages(container, delays);
      },
    });
  }

  // Watchdog. A group whose trigger never fires — inside a scroll container,
  // under a collapsed ancestor, or simply missed — would otherwise stay at
  // opacity 0 for good. Decoration is never allowed to cost content.
  window.setTimeout(() => {
    for (const { container, targets } of groups) {
      const stranded = targets.filter((target) => !target.classList.contains('is-revealed'));
      if (!stranded.length) continue;
      land(stranded);
      gsap.set(container.querySelectorAll('[data-stage-marker]'), { '--stage-pass': 1 });
    }
  }, WATCHDOG_MS);

  return count;
}

/**
 * Scrub `[data-parallax]` layers against scroll position.
 * The reference here is traffic shifting between two planes: the layers move
 * at different rates but stay locked to the same scroll, never drifting.
 */
function initParallax() {
  const layers = [...document.querySelectorAll('[data-parallax]')];
  if (!layers.length) return 0;

  for (const layer of layers) {
    const speed = Number.parseFloat(layer.dataset.parallax) || 0.2;
    gsap.to(layer, {
      y: () => -(window.innerHeight * speed),
      ease: 'none',
      scrollTrigger: {
        trigger: layer,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        invalidateOnRefresh: true,
      },
    });
  }

  return layers.length;
}

/**
 * Boot the motion layer.
 *
 * @returns {{ reveal: number, parallax: number, motion: 'full' | 'reduced' }}
 */
export function initMotion() {
  const prefersReducedMotion = window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;

  if (prefersReducedMotion) {
    // Land everything at once. Nothing was hidden by CSS, so there is nothing
    // to undo — this only settles the class contract the rest of the site and
    // the test suite read.
    land([...document.querySelectorAll('[data-reveal]')]);
    gsap.set('[data-stage-marker]', { '--stage-pass': 1 });
    return { reveal: 0, parallax: 0, motion: 'reduced' };
  }

  // Marks that GSAP owns these elements, so the CSS transitions that would
  // otherwise run alongside its tweens can stand down. See css/motion.css.
  document.documentElement.classList.add('gsap-motion');

  const result = { reveal: initReveal(), parallax: initParallax(), motion: 'full' };

  // If Reduce Motion is switched on mid-session, stop animating and land
  // whatever is still in flight rather than leaving it half-way.
  window.matchMedia?.(REDUCED_MOTION_QUERY).addEventListener('change', (event) => {
    if (!event.matches) return;
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    gsap.globalTimeline.clear();
    land([...document.querySelectorAll('[data-reveal]')]);
    gsap.set('[data-stage-marker]', { '--stage-pass': 1 });
  });

  return result;
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initMotion(), { once: true });
  } else {
    initMotion();
  }
}
